import { logger } from './logger';

export type BreakType = 'water' | 'movement' | 'eyes' | 'full';

export interface HyperfocusBreakSettings {
  breakIntervalMinutes: number;
  breakType: BreakType;
  isEnabled: boolean;
  autoSnoozeMinutes?: number;
  customMessage?: string;
}

export interface BreakReminder {
  id: string;
  scheduledTime: Date;
  breakType: BreakType;
  message: string;
  isActive: boolean;
  snoozeCount: number;
  maxSnoozes: number;
}

export interface BreakSession {
  id: string;
  startTime: Date;
  endTime?: Date;
  breakType: BreakType;
  wasCompleted: boolean;
  skipReason?: string;
  notes?: string;
}

export class HyperfocusBreakService {
  private settings: HyperfocusBreakSettings;
  private currentReminder: BreakReminder | null = null;
  private reminderTimer: NodeJS.Timeout | null = null;
  private settingsKey = 'adhd-hyperfocus-break-settings';
  private sessionsKey = 'adhd-hyperfocus-break-sessions';
  
  private onBreakReminderCallback?: (reminder: BreakReminder) => void;
  private onBreakCompleteCallback?: (session: BreakSession) => void;

  constructor() {
    this.settings = this.loadSettings();
    this.restoreActiveReminder();
  }

  /**
   * Configure hyperfocus break reminders
   */
  setBreakReminders(
    breakIntervalMinutes: number, 
    breakType: BreakType = 'full',
    customOptions?: {
      autoSnoozeMinutes?: number;
      customMessage?: string;
      isEnabled?: boolean;
    }
  ): { success: boolean; message: string } {
    this.settings = {
      breakIntervalMinutes,
      breakType,
      isEnabled: customOptions?.isEnabled ?? true,
      autoSnoozeMinutes: customOptions?.autoSnoozeMinutes,
      customMessage: customOptions?.customMessage
    };

    this.saveSettings();
    
    if (this.settings.isEnabled) {
      this.scheduleNextReminder();
      
      logger.adhdAction('hyperfocus_breaks_configured', {
        breakIntervalMinutes,
        breakType,
        autoSnoozeMinutes: customOptions?.autoSnoozeMinutes,
        hasCustomMessage: !!customOptions?.customMessage
      });

      return {
        success: true,
        message: `Hyperfocus break reminders set for every ${breakIntervalMinutes} minutes (${breakType} breaks)${customOptions?.autoSnoozeMinutes ? ` with ${customOptions.autoSnoozeMinutes}-minute auto-snooze` : ''}`
      };
    } else {
      this.clearReminders();
      return {
        success: true,
        message: 'Hyperfocus break reminders have been disabled'
      };
    }
  }

  /**
   * Enable or disable break reminders
   */
  toggleBreakReminders(enabled: boolean): void {
    this.settings.isEnabled = enabled;
    this.saveSettings();

    if (enabled) {
      this.scheduleNextReminder();
    } else {
      this.clearReminders();
    }

    logger.adhdAction('hyperfocus_breaks_toggled', { enabled });
  }

  /**
   * Snooze the current break reminder
   */
  snoozeReminder(snoozeMinutes: number = 10): boolean {
    if (!this.currentReminder) return false;

    if (this.currentReminder.snoozeCount >= this.currentReminder.maxSnoozes) {
      return false; // Max snoozes reached
    }

    this.currentReminder.snoozeCount++;
    this.currentReminder.scheduledTime = new Date(Date.now() + snoozeMinutes * 60000);
    
    this.clearReminderTimer();
    this.scheduleReminderTimer();

    logger.adhdAction('hyperfocus_break_snoozed', {
      reminderId: this.currentReminder.id,
      snoozeMinutes,
      snoozeCount: this.currentReminder.snoozeCount
    });

    return true;
  }

  /**
   * Complete a break (user took the break)
   */
  completeBreak(notes?: string): BreakSession | null {
    if (!this.currentReminder) return null;

    const session: BreakSession = {
      id: this.generateSessionId(),
      startTime: new Date(),
      endTime: new Date(),
      breakType: this.currentReminder.breakType,
      wasCompleted: true,
      notes
    };

    this.saveBreakSession(session);
    this.clearCurrentReminder();
    this.scheduleNextReminder(); // Schedule the next break

    logger.adhdAction('hyperfocus_break_completed', {
      sessionId: session.id,
      breakType: session.breakType,
      hasNotes: !!notes
    });

    if (this.onBreakCompleteCallback) {
      this.onBreakCompleteCallback(session);
    }

    return session;
  }

  /**
   * Skip a break with a reason
   */
  skipBreak(reason?: string): BreakSession | null {
    if (!this.currentReminder) return null;

    const session: BreakSession = {
      id: this.generateSessionId(),
      startTime: new Date(),
      breakType: this.currentReminder.breakType,
      wasCompleted: false,
      skipReason: reason || 'User chose to skip'
    };

    this.saveBreakSession(session);
    this.clearCurrentReminder();
    this.scheduleNextReminder(); // Schedule the next break

    logger.adhdAction('hyperfocus_break_skipped', {
      sessionId: session.id,
      breakType: session.breakType,
      reason: reason || 'No reason provided'
    });

    return session;
  }

  /**
   * Get current break reminder
   */
  getCurrentReminder(): BreakReminder | null {
    return this.currentReminder;
  }

  /**
   * Get current settings
   */
  getSettings(): HyperfocusBreakSettings {
    return { ...this.settings };
  }

  /**
   * Get time until next break
   */
  getTimeUntilNextBreak(): number {
    if (!this.currentReminder || !this.settings.isEnabled) return 0;
    
    const now = new Date();
    const timeUntil = this.currentReminder.scheduledTime.getTime() - now.getTime();
    return Math.max(0, Math.floor(timeUntil / 60000)); // Return minutes
  }

  /**
   * Set callback for break reminders
   */
  onBreakReminder(callback: (reminder: BreakReminder) => void): void {
    this.onBreakReminderCallback = callback;
  }

  /**
   * Set callback for break completion
   */
  onBreakComplete(callback: (session: BreakSession) => void): void {
    this.onBreakCompleteCallback = callback;
  }

  /**
   * Get break statistics
   */
  getBreakStats(): {
    totalReminders: number;
    breaksCompleted: number;
    breaksSkipped: number;
    completionRate: number;
    averageBreakInterval: number;
    mostCommonSkipReason: string;
  } {
    const sessions = this.getBreakHistory();
    
    if (sessions.length === 0) {
      return {
        totalReminders: 0,
        breaksCompleted: 0,
        breaksSkipped: 0,
        completionRate: 0,
        averageBreakInterval: 0,
        mostCommonSkipReason: 'No data'
      };
    }

    const completed = sessions.filter(s => s.wasCompleted).length;
    const skipped = sessions.length - completed;
    const completionRate = (completed / sessions.length) * 100;

    // Calculate average interval between breaks
    const sortedSessions = sessions.sort((a, b) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    );
    
    let totalInterval = 0;
    for (let i = 1; i < sortedSessions.length; i++) {
      const interval = new Date(sortedSessions[i].startTime).getTime() - 
                      new Date(sortedSessions[i-1].startTime).getTime();
      totalInterval += interval;
    }
    
    const averageBreakInterval = sortedSessions.length > 1 
      ? Math.round(totalInterval / (sortedSessions.length - 1) / 60000) 
      : 0;

    // Find most common skip reason
    const skipReasons: { [reason: string]: number } = {};
    sessions.filter(s => !s.wasCompleted && s.skipReason).forEach(session => {
      const reason = session.skipReason!;
      skipReasons[reason] = (skipReasons[reason] || 0) + 1;
    });

    const mostCommonSkipReason = Object.entries(skipReasons)
      .sort(([,a], [,b]) => b - a)[0]?.[0] || 'No skip reasons recorded';

    return {
      totalReminders: sessions.length,
      breaksCompleted: completed,
      breaksSkipped: skipped,
      completionRate: Math.round(completionRate),
      averageBreakInterval,
      mostCommonSkipReason
    };
  }

  private scheduleNextReminder(): void {
    if (!this.settings.isEnabled) return;

    this.clearCurrentReminder();

    const now = new Date();
    const nextBreakTime = new Date(now.getTime() + this.settings.breakIntervalMinutes * 60000);

    this.currentReminder = {
      id: this.generateReminderId(),
      scheduledTime: nextBreakTime,
      breakType: this.settings.breakType,
      message: this.settings.customMessage || this.getDefaultBreakMessage(this.settings.breakType),
      isActive: true,
      snoozeCount: 0,
      maxSnoozes: 3
    };

    this.saveCurrentReminder();
    this.scheduleReminderTimer();
  }

  private scheduleReminderTimer(): void {
    if (!this.currentReminder) return;

    const timeUntilReminder = this.currentReminder.scheduledTime.getTime() - Date.now();
    
    if (timeUntilReminder <= 0) {
      this.triggerReminder();
      return;
    }

    this.reminderTimer = setTimeout(() => {
      this.triggerReminder();
    }, timeUntilReminder);
  }

  private triggerReminder(): void {
    if (!this.currentReminder) return;

    logger.adhdAction('hyperfocus_break_triggered', {
      reminderId: this.currentReminder.id,
      breakType: this.currentReminder.breakType,
      snoozeCount: this.currentReminder.snoozeCount
    });

    if (this.onBreakReminderCallback) {
      this.onBreakReminderCallback(this.currentReminder);
    }

    // Auto-snooze if configured and no callback is set
    if (this.settings.autoSnoozeMinutes && !this.onBreakReminderCallback) {
      setTimeout(() => {
        if (this.currentReminder) {
          this.snoozeReminder(this.settings.autoSnoozeMinutes!);
        }
      }, 30000); // Auto-snooze after 30 seconds of no response
    }
  }

  private getDefaultBreakMessage(breakType: BreakType): string {
    const messages = {
      water: "💧 Time for a hydration break! Your brain needs water to stay sharp during hyperfocus.",
      movement: "🚶‍♀️ Time to move! A quick stretch or walk will boost your energy and focus.",
      eyes: "👀 Give your eyes a rest! Look away from screens and focus on something 20 feet away for 20 seconds.",
      full: "🌟 Time for a full break! Step away, breathe, hydrate, and reset your focus energy."
    };

    return messages[breakType];
  }

  private clearReminders(): void {
    this.clearReminderTimer();
    this.clearCurrentReminder();
  }

  private clearReminderTimer(): void {
    if (this.reminderTimer) {
      clearTimeout(this.reminderTimer);
      this.reminderTimer = null;
    }
  }

  private clearCurrentReminder(): void {
    this.currentReminder = null;
    localStorage.removeItem('adhd-active-break-reminder');
  }

  private generateReminderId(): string {
    return `br_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateSessionId(): string {
    return `bs_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private saveSettings(): void {
    localStorage.setItem(this.settingsKey, JSON.stringify(this.settings));
  }

  private loadSettings(): HyperfocusBreakSettings {
    try {
      const stored = localStorage.getItem(this.settingsKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load hyperfocus break settings:', error);
    }

    // Default settings
    return {
      breakIntervalMinutes: 30,
      breakType: 'full',
      isEnabled: false
    };
  }

  private saveCurrentReminder(): void {
    if (this.currentReminder) {
      localStorage.setItem('adhd-active-break-reminder', JSON.stringify(this.currentReminder));
    }
  }

  private restoreActiveReminder(): void {
    try {
      const stored = localStorage.getItem('adhd-active-break-reminder');
      if (stored) {
        const reminder: BreakReminder = JSON.parse(stored);
        
        // Check if reminder is still valid
        const now = new Date();
        const scheduledTime = new Date(reminder.scheduledTime);
        
        if (reminder.isActive && this.settings.isEnabled) {
          this.currentReminder = {
            ...reminder,
            scheduledTime
          };
          
          // If the reminder time has passed, trigger it immediately
          if (now >= scheduledTime) {
            this.triggerReminder();
          } else {
            this.scheduleReminderTimer();
          }
        } else {
          localStorage.removeItem('adhd-active-break-reminder');
        }
      }
    } catch (error) {
      console.error('Failed to restore active break reminder:', error);
      localStorage.removeItem('adhd-active-break-reminder');
    }
  }

  private saveBreakSession(session: BreakSession): void {
    try {
      const history = this.getBreakHistory();
      history.push(session);
      
      // Keep only last 100 sessions
      const trimmed = history.slice(-100);
      localStorage.setItem(this.sessionsKey, JSON.stringify(trimmed));
    } catch (error) {
      console.error('Failed to save break session:', error);
    }
  }

  private getBreakHistory(): BreakSession[] {
    try {
      const stored = localStorage.getItem(this.sessionsKey);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('Failed to load break history:', error);
      return [];
    }
  }
}

// Create singleton instance
export const hyperfocusBreakService = new HyperfocusBreakService();